import React, { useEffect, useState } from 'react'
import { flightApi } from '../utils/axiosConfig'
import { FlightDetails } from '../interface/flight.details'
import SingleFlightCheckIn from '../components/SingleFlightCheckIn'
import DoubleFlightCheckIn from '../components/DoubleFlightCheckIn'
import { normalizeCode } from '../utils/common.utils'
import './FlightCheckIn.css';
import { useSocket } from '../context/SocketContext'
import { DeviceDetails } from '../interface/device.details'
import AdvertisementDisplay from '../components/AdvertisementDisplay'
import { useAdSequence } from '../hooks/useAdSequence'
import { AdvertisementType } from '@industronics/fids-utils'
import { apiRoutes } from '../utils/apiRoutes'
import { ADS_URL } from '../utils/env-utils'
interface FlightCheckInPageProp {
    device: DeviceDetails
}

const FlightCheckInPage: React.FC<FlightCheckInPageProp> = ({ device }) => {
    const { socket } = useSocket();
    const [flights, setFlights] = useState<FlightDetails[]>([])
    const [initialLoadDone, setInitialLoadDone] = useState(false)
    const { currentAd } = useAdSequence(device.advertisement);

    const isFullScreenAd = currentAd &&
        !currentAd.ratio &&
        currentAd.advertisementType === AdvertisementType.FullScreen &&
        currentAd.type === 'ad';

    useEffect(() => {
        const fetchFlights = async () => {
            try {
                const response = await flightApi.get(apiRoutes.flightTrips.getFlightTrips)

                setFlights(response.data.data || [])
                setInitialLoadDone(true)
            } catch (err) {
                console.error('Failed to fetch flight', err)
                setInitialLoadDone(true)
            }
        }

        fetchFlights()
    }, []);

    useEffect(() => {
        if (!socket) return;

        const handleFlightUpdate = (flightData: FlightDetails) => {
            const targetDesk = normalizeCode(device.checkInCounter?.toLowerCase?.() || '');

            const found = flightData?.checkInDesks?.find(x => x.counter.toLowerCase() === targetDesk);
            if (flightData?.checkInDesks?.length && found) {
                if (!found.openingTime || !found.closingTime) return;

                const now = new Date();
                const opening = new Date(found.openingTime);
                const closing = new Date(found.closingTime);

                if (opening <= now && closing >= now) {
                    setFlights((prev) => {
                        const index = prev.findIndex((x) => x._id === flightData._id);
                        let updatedFlights = [...prev];

                        if (index === -1) {
                            updatedFlights.push(flightData)
                        } else {
                            updatedFlights[index] = flightData;
                        }
                        return updatedFlights.filter(
                            (item) =>
                                !item.isDeleted &&
                                item.isDisplay &&
                                (new Date(item.effectiveTime).getTime() > Date.now())
                        )
                    })
                } else {
                    setFlights((prev) => prev.filter((x) => x._id !== flightData._id));
                }
            } else {
                setFlights((prev) => prev.filter((x) => x._id !== flightData._id));
            }
        };

        const tripListener = () => {
            socket.off("flight.update", handleFlightUpdate);
            socket.on("flight.update", handleFlightUpdate);
        };

        const handleConnect = () => {
            tripListener();
        };

        socket.on("connect", handleConnect);

        if (socket.connected) {
            handleConnect();
        }

        return () => {
            socket.off("connect", handleConnect);
            socket.off("flight.update", handleFlightUpdate);
        };
    }, [socket]);


    return (
        <div className="flight-board-container container-fluid p-0">
            {isFullScreenAd ? (
                <AdvertisementDisplay advertisement={currentAd} isFullScreen={true} />
            ) : (
                <>
                    {!initialLoadDone ? null : flights.length > 0 ? (
                        flights.length === 1 ? (
                            <SingleFlightCheckIn flight={flights[0]} />
                        ) : (
                            <DoubleFlightCheckIn flights={flights} />
                        )
                    ) : (
                        <img
                            src={device.cluster?.settings?.defaultDisplayImage
                                ? `${ADS_URL}/cluster-settings/${device.cluster.settings.defaultDisplayImage}`
                                : '/default.avif'}
                            alt="Default display"
                        />
                    )}

                    {currentAd && currentAd.ratio && (
                        <AdvertisementDisplay advertisement={currentAd} isFullScreen={false} />
                    )}
                </>
            )}
        </div>
    )
}

export default FlightCheckInPage
